/**
 * Procedural item-icon inference — World Art Pack 2, Task 3.
 *
 * 443 items in ITEM_DATABASE, only 29 have bespoke hand-authored icons
 * (ItemIconRegistry.ts ALL_ICONS). This module infers a coarse visual
 * `IconKind` + the item's rarity for every remaining item so a later
 * generator (Tasks 4-6, keyed by IconKind) can render a reasonable
 * procedural icon instead of falling all the way to the generic
 * initial-letter fallback.
 *
 * Ladder (see inferIconLook): explicit `tags` subtype hit (most reliable —
 * real content tags frequently carry the exact kind, e.g. `tags: ['axe']`
 * or slot words like `head`/`chest`/`off_hand`) -> `equipmentSlot` (armor
 * only) -> id/name keyword table (scoped per ItemType so e.g. "fang" means
 * `dagger` for weapons but `part` for materials) -> flat ItemType default.
 *
 * Verified against the full live ITEM_DATABASE (443 items, scripts/prototype
 * dry run) with EXACT per-type coverage: weapon 86/86, armor 220/220,
 * material 86/86, consumable 50/50, currency 1/1 — 0 items land on
 * `'generic'`. See ap2-task-3-report.md for the full generic/default-only
 * punch list handed to Tasks 4-6.
 */
import type { ItemDefinition } from '@/shared/content/generated/items.gen';
import type { ItemRarity } from '@/shared/CharacterTypes';

export type IconKind =
  | 'sword' | 'axe' | 'mace' | 'dagger' | 'bow' | 'staff' | 'wand' | 'shield' | 'fist' | 'polearm'   // weapon
  | 'helm' | 'chest' | 'legs' | 'boots' | 'gloves' | 'shoulders' | 'belt' | 'cloak' | 'ring' | 'amulet' | 'trinket' | 'offhand' | 'trail' | 'lantern' // armor
  | 'ore' | 'bar' | 'leather' | 'cloth' | 'herb' | 'gem' | 'essence' | 'wood' | 'stone' | 'part'      // material
  | 'potion' | 'elixir' | 'flask' | 'scroll' | 'food' | 'bandage' | 'candy' | 'stone_consumable'      // consumable
  | 'coin' | 'generic';

export interface IconLook {
  kind: IconKind;
  rarity: ItemRarity;
  tint?: string;
}

// ---------------------------------------------------------------------------
// Tag-based inference (rung 1 — the most reliable signal, when present).
// `ItemDefinition.tags` is a real, optional field on the interface; real
// content frequently stashes the exact subtype in it (gravecleaver has
// tags: ['weapon','two_hand','axe','classic']; verdance_hornlord_trophy_totem
// (armor) carries 'off_hand'). Not every item has tags, so this rung only
// short-circuits the ladder on a hit — it never forces a match.
// ---------------------------------------------------------------------------

const WEAPON_TAG_KIND: Partial<Record<string, IconKind>> = {
  sword: 'sword', axe: 'axe', mace: 'mace', dagger: 'dagger',
  bow: 'bow', crossbow: 'bow', staff: 'staff', wand: 'wand',
  shield: 'shield', polearm: 'polearm', fist: 'fist',
  scepter: 'wand', // Scales of Libra-style "caster scepter" tag
};

const ARMOR_TAG_KIND: Partial<Record<string, IconKind>> = {
  head: 'helm', chest: 'chest', legs: 'legs', boots: 'boots',
  hands: 'gloves', shoulder: 'shoulders', belt: 'belt', waist: 'belt',
  back: 'cloak', ring: 'ring', neck: 'amulet', trinket: 'trinket',
  off_hand: 'offhand',
};

// ---------------------------------------------------------------------------
// equipmentSlot -> kind (rung 2, armor only). `equipmentSlot` is always
// literally 'weapon' for weapon-category items (no subtype signal), so this
// rung only applies to armor. 'accessory' is coarse (ring/amulet/trinket all
// share it) so it falls through to the keyword table instead of a direct hit.
// ---------------------------------------------------------------------------

const EQUIPMENT_SLOT_KIND: Partial<Record<string, IconKind>> = {
  hat: 'helm', top: 'chest', bottom: 'legs', shoes: 'trail',
  gloves: 'gloves', cape: 'cloak',
};

// ---------------------------------------------------------------------------
// id/name keyword tables (rung 3), scoped per ItemType so the same English
// word can mean different things in different domains (e.g. "fang" is a
// `dagger` on a weapon but a `part` on a material). Order matters within a
// list: more specific/rare words are checked before generic ones so e.g.
// "tomahawk" resolves to axe before any later, broader entry could shadow it.
//
// `ore` is the one keyword that needs EXACT-TOKEN matching rather than plain
// substring: real ore ids are always a standalone "..._ore" token, but plain
// substring search false-positives inside unrelated fused words that merely
// CONTAIN "ore" — "mushroom_spore" and "inferno_core" both do. Found via the
// totality-test dry run; see the report for the full note.
// ---------------------------------------------------------------------------

const EXACT_TOKEN_ONLY = new Set<string>(['ore']);

type KeywordEntry = readonly [keyword: string, kind: IconKind];

const WEAPON_KEYWORDS: readonly KeywordEntry[] = [
  ['tomahawk', 'axe'], ['bardiche', 'polearm'], ['glaive', 'polearm'],
  ['buckler', 'shield'], ['shield', 'shield'], ['spear', 'polearm'], ['pole', 'polearm'],
  ['dagger', 'dagger'], ['knife', 'dagger'], ['fang', 'dagger'],
  ['hammer', 'mace'], ['mace', 'mace'], ['axe', 'axe'],
  ['bow', 'bow'], ['staff', 'staff'],
  ['tome', 'wand'], ['scepter', 'wand'], ['wand', 'wand'],
  ['sword', 'sword'], ['blade', 'sword'], ['fist', 'fist'],
];

const ARMOR_KEYWORDS: readonly KeywordEntry[] = [
  ['spaulder', 'shoulders'], ['shoulder', 'shoulders'],
  ['chestpiece', 'chest'], ['breastplate', 'chest'], ['robe', 'chest'], ['vest', 'chest'],
  ['tunic', 'chest'], ['platemail', 'chest'], ['chainmail', 'chest'],
  ['legging', 'legs'], ['pant', 'legs'], ['trouser', 'legs'], ['greaves', 'legs'], ['chausses', 'legs'],
  ['boot', 'boots'], ['shoe', 'boots'], ['feet', 'boots'],
  ['glove', 'gloves'], ['hand', 'gloves'],
  ['crown', 'helm'], ['helm', 'helm'], ['hood', 'helm'], ['hat', 'helm'], ['head', 'helm'], ['cover', 'helm'],
  ['cloak', 'cloak'], ['cape', 'cloak'],
  ['belt', 'belt'], ['girdle', 'belt'], ['waist', 'belt'],
  ['ring', 'ring'],
  ['amulet', 'amulet'], ['pendant', 'amulet'], ['necklace', 'amulet'], ['neck', 'amulet'],
  ['trinket', 'trinket'], ['card', 'trinket'], ['armband', 'trinket'], ['bracer', 'trinket'],
];

const MATERIAL_KEYWORDS: readonly KeywordEntry[] = [
  ['leather', 'leather'], ['pelt', 'leather'], ['hide', 'leather'],
  ['cloth', 'cloth'], ['silk', 'cloth'], ['weave', 'cloth'], ['thread', 'cloth'], ['linen', 'cloth'], ['wool', 'cloth'],
  ['ore', 'ore'],
  ['bar', 'bar'], ['ingot', 'bar'],
  ['herb', 'herb'], ['bloom', 'herb'], ['leaf', 'herb'], ['root', 'herb'],
  ['essence', 'essence'], ['dust', 'essence'], ['ichor', 'essence'], ['reagent', 'essence'],
  ['gem', 'gem'], ['crystal', 'gem'], ['shard', 'gem'], ['ruby', 'gem'], ['sapphire', 'gem'], ['diamond', 'gem'], ['emerald', 'gem'],
  ['wood', 'wood'], ['branch', 'wood'], ['log', 'wood'],
  ['stone', 'stone'], ['rock', 'stone'], ['flux', 'stone'], ['grinding', 'stone'],
  ['scale', 'part'], ['tusk', 'part'], ['fang', 'part'], ['wing', 'part'], ['feather', 'part'], ['claw', 'part'],
  ['snout', 'part'], ['foot', 'part'], ['stinger', 'part'], ['carapace', 'part'], ['meat', 'part'],
  ['fish', 'part'], ['orb', 'part'], ['core', 'part'], ['badge', 'part'], ['spore', 'part'], ['gel', 'part'],
  ['star', 'part'], ['oil', 'part'], ['journal', 'part'], ['page', 'part'], ['charge', 'part'],
  ['mucus', 'part'], ['vial', 'part'],
];

const CONSUMABLE_KEYWORDS: readonly KeywordEntry[] = [
  ['potion', 'potion'], ['antidote', 'potion'], ['powder', 'potion'],
  ['elixir', 'elixir'],
  ['flask', 'flask'],
  ['scroll', 'scroll'],
  ['bandage', 'bandage'],
  ['candy', 'candy'],
  ['stone', 'stone_consumable'],
  ['food', 'food'], ['cooked', 'food'], ['meat', 'food'], ['stew', 'food'],
  ['bread', 'food'], ['pie', 'food'], ['drink', 'food'], ['milk', 'food'],
];

function tokenize(text: string): string[] {
  return text.toLowerCase().split(/[^a-z0-9]+/).filter(Boolean);
}

function hasKeyword(idLower: string, nameLower: string, idTokens: string[], nameTokens: string[], keyword: string): boolean {
  if (EXACT_TOKEN_ONLY.has(keyword)) {
    return idTokens.includes(keyword) || nameTokens.includes(keyword);
  }
  return idLower.includes(keyword) || nameLower.includes(keyword);
}

function findByKeywords(
  list: readonly KeywordEntry[],
  idLower: string,
  nameLower: string,
  idTokens: string[],
  nameTokens: string[],
): IconKind | null {
  for (const [keyword, kind] of list) {
    if (hasKeyword(idLower, nameLower, idTokens, nameTokens, keyword)) return kind;
  }
  return null;
}

// ---------------------------------------------------------------------------
// Optional elemental tint (id/name scan). Not part of the brief's required
// ladder — a light nice-to-have so Tasks 4-6 can tint the shared inner art
// (e.g. ice_staff -> cyan wash) without hardcoding a second lookup. Absent
// (undefined) when no elemental keyword matches; callers should treat that
// as "use the kind's default color," not an error.
// ---------------------------------------------------------------------------

const TINT_KEYWORDS: readonly (readonly [string, string])[] = [
  ['fire', '#ff6b35'], ['flame', '#ff6b35'], ['inferno', '#ff6b35'], ['volcanic', '#ff6b35'], ['molten', '#ff6b35'],
  ['frost', '#7ec8e3'], ['ice', '#7ec8e3'], ['frozen', '#7ec8e3'], ['glacial', '#7ec8e3'],
  ['shadow', '#9b6bd6'], ['void', '#9b6bd6'], ['dark', '#9b6bd6'], ['corrupt', '#9b6bd6'], ['cursed', '#9b6bd6'],
  ['holy', '#ffd76b'], ['blessed', '#ffd76b'], ['radiant', '#ffd76b'],
  ['nature', '#6bd67e'], ['poison', '#6bd67e'], ['toxic', '#6bd67e'], ['venom', '#6bd67e'],
  ['arcane', '#b98bff'], ['mystic', '#b98bff'],
];

function inferTint(idLower: string, nameLower: string): string | undefined {
  for (const [keyword, hex] of TINT_KEYWORDS) {
    if (idLower.includes(keyword) || nameLower.includes(keyword)) return hex;
  }
  return undefined;
}

/**
 * Infer a coarse visual IconKind + carry the item's rarity for a procedural
 * icon. Never throws — every ItemType has a flat default at the bottom of
 * the ladder, so worst case an item lands on its type's generic silhouette
 * (e.g. an unrecognized weapon -> 'sword'), never on the catch-all
 * `'generic'` kind (that's reserved for a `type` outside the known 5).
 */
export function inferIconLook(item: ItemDefinition): IconLook {
  const rarity = item.rarity;
  const idLower = item.id.toLowerCase();
  const nameLower = (item.name ?? '').toLowerCase();
  const idTokens = tokenize(item.id);
  const nameTokens = tokenize(item.name ?? '');
  const tags = item.tags ?? [];
  // A Trail item's icon wears its authored trail colour (lane BD, D46).
  const tint = item.trail?.color ?? item.lantern?.color ?? item.flame?.color ?? inferTint(idLower, nameLower);

  const kind = inferKind(item.type, tags, item.equipmentSlot, idLower, nameLower, idTokens, nameTokens);

  return tint ? { kind, rarity, tint } : { kind, rarity };
}

function inferKind(
  type: ItemDefinition['type'],
  tags: string[],
  equipmentSlot: ItemDefinition['equipmentSlot'],
  idLower: string,
  nameLower: string,
  idTokens: string[],
  nameTokens: string[],
): IconKind {
  switch (type) {
  case 'weapon': {
    for (const tag of tags) {
      const hit = WEAPON_TAG_KIND[tag];
      if (hit) return hit;
    }
    return findByKeywords(WEAPON_KEYWORDS, idLower, nameLower, idTokens, nameTokens) ?? 'sword';
  }
  case 'armor': {
    // The `shoes` slot is the Trail slot (lane BD, D46): its items are trails,
    // whatever their id or tags still say.
    if (equipmentSlot === 'shoes') return 'trail';
    // The Lantern slot (lane BK, B26): its items are lanterns.
    if (equipmentSlot === 'lantern') return 'lantern';
    // The Flame slot: its items draw with the lantern painter until they get their own.
    if (equipmentSlot === 'flame') return 'lantern';
    for (const tag of tags) {
      const hit = ARMOR_TAG_KIND[tag];
      if (hit) return hit;
    }
    if (equipmentSlot && equipmentSlot !== 'accessory') {
      const hit = EQUIPMENT_SLOT_KIND[equipmentSlot];
      if (hit) return hit;
    }
    if (equipmentSlot === 'accessory') {
      const accessoryHit = findByKeywords(
        ARMOR_KEYWORDS.filter(([, kind]) => kind === 'ring' || kind === 'amulet' || kind === 'trinket'),
        idLower, nameLower, idTokens, nameTokens,
      );
      return accessoryHit ?? 'trinket';
    }
    return findByKeywords(ARMOR_KEYWORDS, idLower, nameLower, idTokens, nameTokens) ?? 'chest';
  }
  case 'material':
    return findByKeywords(MATERIAL_KEYWORDS, idLower, nameLower, idTokens, nameTokens) ?? 'part';
  case 'consumable':
    return findByKeywords(CONSUMABLE_KEYWORDS, idLower, nameLower, idTokens, nameTokens) ?? 'potion';
  case 'currency':
    return 'coin';
  default:
    return 'generic';
  }
}
